import {
  Button,
  Card,
  CardContent,
  Container,
  Dialog,
  DialogContent,
  DialogContentText,
  IconButton,
  Step,
  StepLabel,
  Stepper,
  Typography
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { useAssignInfo } from "@saleor/accountSettings/misc";
import { useAccountAssignMall } from "@saleor/accountSettings/queries";
import FormSpacer from "@saleor/components/FormSpacer";
import DropOffManageRacksAndBins from "@saleor/dropOff/components/DropOffDispatch/DropOffManageRacksAndBins";
import {
  useTemplateCreate,
  useTemplateDelete,
  useTemplateFinalize,
  useTemplateUpdate
} from "@saleor/dropOff/mutation";
import { useGetAvailableTemplatesQuery } from "@saleor/dropOff/queries";
import { useTemplateStyles } from "@saleor/dropOff/styles/templates";
import { GetAvailableTemplates_templates_edges_node } from "@saleor/dropOff/types/GetAvailableTemplates";
import {
  dropOffCreateTemplate,
  dropOffListPath,
  dropOffListUrl,
  DropOffListUrlQueryParams
} from "@saleor/dropOff/url";
import { TabValuesEnum } from "@saleor/dropOff/utils/filters";
import {
  TemplateAction,
  userCanManageTaskAreas
} from "@saleor/dropOff/utils/handlers";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import useUser from "@saleor/hooks/useUser";
import {
  SmopUserTypeCode,
  TemplateStatus,
  TemplateStatusEnum
} from "@saleor/types/globalTypes";
import { handleGraphQLErrors } from "@saleor/utils/handlers/errorHandler";
import { stringifyQs } from "@saleor/utils/urls";
import * as React from "react";
import { FormattedMessage } from "react-intl";
import urlJoin from "url-join";

import DropOffHomeLocationAndTaskArea from "../../components/DropOffHomeLocation/DropOffHomeLocationAndTaskArea";
import {
  DropOffStepIcon,
  DropOffStepperConnector
} from "../../components/DropOffStepper/DropOffStepper";
import DropOffManageTaskAreas from "../../components/DropOffTaskArea/DropOffManageTaskAreas";
import DropOffNameTemplate from "../../components/DropOffTemplate/DropOffNameTemplate";

interface DropOffCreateNewTemplateProps {
  params: DropOffListUrlQueryParams;
}

const DropOffCreateNewTemplate: React.FC<DropOffCreateNewTemplateProps> = props => {
  const { params } = props;

  const classes = useTemplateStyles(props);
  const notify = useNotifier();
  const navigate = useNavigator();
  const { user } = useUser();
  const userInfo = useAssignInfo(user);

  const [currentStep, setCurrentStep] = React.useState(0);
  const [templateId, setTemplateId] = React.useState("");
  const [templateName, setTemplateName] = React.useState("");
  const [selectedMallId, setSelectedMallId] = React.useState("");
  const [selectedMallName, setSelectedMallName] = React.useState("");
  const [displayDialog, setDisplayDialog] = React.useState(false);
  const [newActiveTemplate, setNewActiveTemplate] = React.useState(false);

  const [activeTemplates, setActiveTemplates] = React.useState<
    GetAvailableTemplates_templates_edges_node[]
  >([]);
  const [availableTemplates, setAvailableTemplates] = React.useState<
    GetAvailableTemplates_templates_edges_node[]
  >([]);

  const steps = [
    "NAME YOUR TEMPLATE",
    "CREATE HOME LOCATIONS & TASK AREAS",
    "ADD DISPATCH RACKS AND BINS",
    "ASSIGN STORES"
  ];

  const isOmiOPS: boolean =
    user?.accessGroup?.smopUsertypeCode === SmopUserTypeCode.OMI_OPS;

  const hasMall = userInfo?.mall != null && userInfo?.mall.trim().length > 0;

  const { data: accountMallAssignmentData } = useAccountAssignMall({
    displayLoader: true,
    skip: !hasMall && (!selectedMallId || selectedMallId.length === 0),
    variables: {
      id: hasMall ? userInfo?.mall : selectedMallId
    }
  });

  const { data: templates } = useGetAvailableTemplatesQuery({
    displayLoader: true,
    skip: !hasMall && (!selectedMallId || selectedMallId.length === 0),
    variables: {
      filter: {
        mall: hasMall ? userInfo?.mall : selectedMallId
      },
      first: 100
    }
  });

  const [createTemplate] = useTemplateCreate({
    onCompleted: e => {
      if (e.templateCreate.templateError.length === 0) {
        setTemplateId(e.templateCreate.template.id);
        setTemplateName(e.templateCreate.template.name);
        setSelectedMallName(e.templateCreate.template.mall.name);
        setCurrentStep(1);
        updateUrlStep(1);
      } else {
        notify({
          status: "error",
          text: e.templateCreate.templateError[0].message
        });
      }
    },
    onError: handleGraphQLErrors
  });

  const [updateTemplate] = useTemplateUpdate({
    onCompleted: e => {
      if (e.templateUpdate.templateError.length === 0) {
        setSelectedMallName(e.templateUpdate.template.mall.name);
        setTemplateName(e.templateUpdate.template.name);
        setCurrentStep(1);
        updateUrlStep(1);
      } else {
        notify({
          status: "error",
          text: e.templateUpdate.templateError[0].message
        });
      }
    },
    onError: handleGraphQLErrors
  });

  const [deleteTemplate] = useTemplateDelete({
    onCompleted: null,
    onError: handleGraphQLErrors
  });

  const navigateToDropOffHomepage = () => {
    if (!params?.mall || params?.mall.length === 0) {
      navigate(dropOffListUrl());
    } else {
      navigate(
        urlJoin(
          dropOffListPath,
          "?" +
            stringifyQs({
              mall: params?.mall,
              tab: TabValuesEnum.TEMPLATES
            })
        )
      );
    }
  };

  const [finalizeInactiveTemplate] = useTemplateUpdate({
    onCompleted: e => {
      if (e.templateUpdate.templateError.length === 0) {
        notify({
          status: "success",
          text: "Template setup successful!"
        });
        navigateToDropOffHomepage();
      } else {
        notify({
          status: "error",
          text: e.templateUpdate.templateError[0].message
        });
      }
    },
    onError: handleGraphQLErrors
  });

  const [finalizeTemplate] = useTemplateFinalize({
    onCompleted: e => {
      if (e.templateFinalize.templateError.length === 0) {
        notify({
          status: "success",
          text: "Template setup successful!"
        });
        navigateToDropOffHomepage();
      } else {
        notify({
          status: "error",
          text: e.templateFinalize.templateError[0].message
        });
      }
    },
    onError: handleGraphQLErrors
  });

  const handleFinishSetup = (templateId, mallId) => {
    if (newActiveTemplate) {
      finalizeTemplate({
        variables: {
          id: templateId
        }
      });
    } else {
      finalizeInactiveTemplate({
        variables: {
          id: templateId,
          input: {
            mall: mallId,
            name: templateName,
            status: TemplateStatusEnum.INACTIVE
          }
        }
      });
    }
  };

  const updateUrlStep = step => {
    navigate(
      urlJoin(
        dropOffCreateTemplate,
        "?" +
          stringifyQs({
            mall: params?.mall,
            step
          })
      )
    );
  };

  React.useEffect(() => {
    setSelectedMallId(params?.mall);
  }, [params?.mall]);

  React.useEffect(() => {
    if (params?.step) {
      setCurrentStep(Number(params?.step));
    }
  }, [params?.step]);

  React.useEffect(() => {
    setAvailableTemplates(templates?.templates?.edges.map(edge => edge.node));
  }, [templates?.templates?.edges]);

  React.useEffect(() => {
    if (availableTemplates && availableTemplates.length > 0) {
      const templates = availableTemplates.filter(
        item => item.status === TemplateStatus.ACTIVE
      );
      setActiveTemplates(templates);
    }
  }, [availableTemplates]);

  React.useEffect(() => {
    const haveActiveTemplateAlready = !(
      activeTemplates && activeTemplates.length > 0
    );
    setNewActiveTemplate(haveActiveTemplateAlready);
  }, [activeTemplates]);

  return (
    <Container className={classes.dropOffContainer}>
      <div className={classes.headerSection}>
        <div className={classes.headerLeft}>
          <div className={classes.headerTitleMall}>
            <h5 className={classes.headerTitle}>{"New Template"}</h5>
            <Typography color="primary" className={classes.mallTitle}>
              {accountMallAssignmentData?.mall?.name || selectedMallName}
            </Typography>
          </div>
        </div>
        <div className={classes.headerLeft}>
          <Button
            size="large"
            className={classes.btnTopViewAllMalls}
            color="primary"
            variant="outlined"
            onClick={() => setDisplayDialog(true)}
          >
            <FormattedMessage
              defaultMessage="Cancel Setup"
              description="button"
            />
          </Button>
        </div>
      </div>

      <Card className={classes.dropOffCard}>
        <CardContent>
          <div className={classes.GeneralInformationRoot}>
            <Stepper
              className={classes.padding0}
              activeStep={Number(currentStep)}
              alternativeLabel
              connector={<DropOffStepperConnector />}
            >
              {steps.map(label => (
                <Step key={label}>
                  <StepLabel
                    StepIconComponent={DropOffStepIcon}
                    classes={{
                      active: classes.stepLabelActive,
                      completed: classes.stepLabelCompleted
                    }}
                  >
                    {label}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>

            <FormSpacer spacing={10} />

            {Number(currentStep) === 0 && (
              <DropOffNameTemplate
                templateNameProps={templateName}
                isUpdate={templateId !== ""}
                isOmiOPS={isOmiOPS}
                mallId={accountMallAssignmentData?.mall?.id || selectedMallId}
                newActiveTemplate={newActiveTemplate}
                activeTemplates={activeTemplates}
                availableTemplates={availableTemplates}
                onCreateTemplate={input => {
                  setSelectedMallId(input.mall);
                  createTemplate({
                    variables: {
                      input
                    }
                  });
                }}
                onUpdateTemplate={input => {
                  setSelectedMallId(input.mall);
                  updateTemplate({
                    variables: {
                      id: templateId,
                      input
                    }
                  });
                }}
                onSelectChange={input => {
                  setSelectedMallId(input);
                }}
                onSetAsActiveTemplate={input => {
                  setNewActiveTemplate(input);
                }}
              />
            )}
            {Number(currentStep) === 1 && (
              <DropOffHomeLocationAndTaskArea
                mallId={accountMallAssignmentData?.mall?.id || selectedMallId}
                templateId={templateId}
                templateAction={TemplateAction.CREATE}
                editHomeLocation={null}
                nextStep={() => {
                  setCurrentStep(2);
                  updateUrlStep(2);
                }}
                onBack={() => {
                  setCurrentStep(0);
                  updateUrlStep(0);
                }}
              />
            )}
            {Number(currentStep) === 2 && (
              <DropOffManageRacksAndBins
                key={"setupTA" + templateId}
                nextStep={() => {
                  setCurrentStep(3);
                  updateUrlStep(3);
                }}
                onBack={() => {
                  setCurrentStep(1);
                  updateUrlStep(1);
                }}
                templateId={templateId}
                templateAction={TemplateAction.CREATE}
              />
            )}
            {Number(currentStep) === 3 ? (
              <DropOffManageTaskAreas
                key={"setupTA" + templateId}
                disableRename={!userCanManageTaskAreas(user)}
                disableAddRemove={!userCanManageTaskAreas(user)}
                mallId={accountMallAssignmentData?.mall?.id || selectedMallId}
                templateId={templateId}
                templateAction={TemplateAction.CREATE}
                prevStep={() => {
                  setCurrentStep(2);
                  updateUrlStep(2);
                }}
                handleFinishSetup={() => {
                  handleFinishSetup(
                    templateId,
                    accountMallAssignmentData?.mall?.id || selectedMallId
                  );
                }}
              />
            ) : (
              <></>
            )}
          </div>
        </CardContent>
      </Card>

      {/* CANCEL SETUP DIALOG */}
      <Dialog
        open={displayDialog}
        aria-labelledby="dialog-cancel-title"
        aria-describedby="dialog-cancel"
        maxWidth="sm"
      >
        <DialogContent>
          <DialogContentText id="dialog-cancel">
            <div
              style={{
                alignContent: "center",
                display: "flex",
                justifyContent: "center",
                marginBottom: 10
              }}
            >
              <Typography className={classes.templateDialogTitle} variant="h5">
                Cancel Template Setup
              </Typography>
              <IconButton
                aria-label="close"
                className={classes.closeButton}
                onClick={() => setDisplayDialog(false)}
              >
                <CloseIcon />
              </IconButton>
            </div>
            <div className={classes.centerContent}>
              <p className={classes.modalContentText}>
                Are you sure you want to cancel setting up the template?
              </p>
              <p className={classes.modalContentText}>
                All changes will be not be saved.
              </p>
            </div>

            <div className={classes.buttonActionCenter}>
              <Button
                onClick={() => setDisplayDialog(false)}
                color="primary"
                variant="outlined"
                className={classes.displayFlexMarginRight20}
              >
                CANCEL
              </Button>
              <Button
                onClick={() => {
                  if (templateId) {
                    deleteTemplate({
                      variables: {
                        id: templateId
                      }
                    });
                  }
                  navigate(dropOffListPath);
                }}
                color="primary"
                variant="contained"
                className={classes.buttonColor}
              >
                <Typography className={classes.textWhite}>YES</Typography>
              </Button>
            </div>
          </DialogContentText>
        </DialogContent>
      </Dialog>
      {/* CANCEL SETUP DIALOG */}
    </Container>
  );
};

export default DropOffCreateNewTemplate;
